import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Cog, Fan, Plane, Wrench, Radar, Satellite, ArrowRight } from "lucide-react";
import { PRODUCTS } from "@/data/content";
import { useSiteContent } from "@/context/SiteContentContext";
import { staggerContainer, staggerItem } from "../site/Reveal";

const ICONS = { Cog, Fan, Plane, Wrench, Radar, Satellite };

export const ProductsSection = () => {
  const { home } = useSiteContent();
  const cards = home.whatWeBuild?.length ? home.whatWeBuild : PRODUCTS;
  return (
    <section data-testid="products-section" className="relative bg-aero-surface">
      <div className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8 sm:py-28">
        <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <span className="font-heading text-xs uppercase tracking-[0.28em] text-saffron">
              What We Build
            </span>
            <h2 className="mt-4 font-heading text-4xl font-bold tracking-tight text-ink sm:text-5xl">
              From Engines to defence-grade UAS.
            </h2>
          </div>
          <Link
            to="/products"
            data-testid="products-view-all-link"
            className="group inline-flex items-center gap-2 font-heading text-sm font-semibold text-saffron"
          >
            View all products
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
        >
          {cards.map((p) => {
            const Icon = ICONS[p.icon] || Plane;
            return (
              <motion.div key={p.id} variants={staggerItem}>
                <Link
                  to="/products"
                  data-testid={`product-card-${p.id}`}
                  className="group relative flex h-full min-h-[320px] flex-col overflow-hidden rounded-xl border border-steel/20 bg-aero-deep p-7 transition-all duration-500 hover:-translate-y-1 hover:border-saffron/60"
                >
                  {p.bg && (
                    <div
                      aria-hidden
                      className="absolute inset-0 bg-white bg-contain bg-center bg-no-repeat opacity-95 transition-all duration-500 group-hover:opacity-100 group-hover:scale-[1.03]"
                      style={{ backgroundImage: `url(${p.bg})` }}
                    />
                  )}
                  {/* Legibility wash — light left-to-right gradient keeps text readable while image stays vivid */}
                  <div className="absolute inset-0 bg-gradient-to-r from-white/85 via-white/25 to-transparent transition-opacity duration-500 group-hover:from-white/70 group-hover:via-white/10" />
                  <div className="blueprint-grid absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-15" />
                  <div className="relative">
                    <span className="grid h-14 w-14 place-items-center rounded-lg border border-steel/30 bg-aero-navy text-saffron transition-colors duration-500 group-hover:border-saffron">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h3 className="mt-6 font-heading text-xl font-semibold text-ink">
                      {p.title}
                    </h3>
                    <p className="mt-3 max-w-[22rem] font-body text-sm leading-relaxed text-steel">
                      {p.blurb}
                    </p>
                  </div>
                  <span className="relative mt-auto inline-flex items-center gap-2 pt-6 font-heading text-sm font-semibold text-saffron">
                    View More
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
